import { type HTMLAttributes } from 'react'
import { cn } from '../../lib/utils'

type BadgeVariant = 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'info'

interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant
  dot?: boolean
}

const variantClasses: Record<BadgeVariant, string> = {
  default: 'bg-surface-elevated text-ink-muted',
  primary: 'bg-brand-primary/20 text-zinc-900 dark:text-white border border-brand-primary/30',
  success: 'bg-green-500/15 text-zinc-900 dark:text-white border border-green-500/25',
  warning: 'bg-amber-500/15 text-zinc-900 dark:text-white border border-amber-500/25',
  danger: 'bg-red-500/15 text-zinc-900 dark:text-white border border-red-500/25',
  info: 'bg-brand-secondary/15 text-zinc-900 dark:text-white border border-brand-secondary/25',
}

const dotClasses: Record<BadgeVariant, string> = {
  default: 'bg-ink-faint',
  primary: 'bg-brand-primary-light',
  success: 'bg-green-400',
  warning: 'bg-amber-400',
  danger: 'bg-red-400',
  info: 'bg-brand-secondary-light',
}

export default function Badge({ variant = 'default', dot = false, className, children, ...props }: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium',
        variantClasses[variant],
        className,
      )}
      {...props}
    >
      {dot && (
        <span className={cn('h-1.5 w-1.5 rounded-full shrink-0', dotClasses[variant])} />
      )}
      {children}
    </span>
  )
}
